<script setup>
import { aboutMarqueeConfig } from '@/configs/about-marquee';
import aboutIcons from '@/configs/about-icons';
import { processIconsInText } from '@/utils/icons-in-text';

import { scrollToBenefits } from '@/utils/common';
import { useRtlVersion } from '@/composables/useRtlVersion';

const { t, tm, locale, mergeLocaleMessage } = useI18n();

const rawDescription = tm('about.description');
const processedDescription = processIconsInText(rawDescription);

mergeLocaleMessage(locale.value, {
  about: {
    processedDescription: processedDescription,
  },
});

const description = t('about.processedDescription', aboutIcons).replaceAll(
  '<strong>',
  '<strong class="section-about__font section-about__font--accent">',
);

const benefits = t('about.benefits').replaceAll(
  '<strong>',
  '<strong class="section-about__font section-about__font--accent">',
);

const { openContactModal } = useContactModal();

const { tp } = useTypograf();

const { isRtlVersion } = useRtlVersion();
</script>

<template>
  <div class="section-about">
    <div class="container">
      <div class="section-about__inner">
        <p class="section-about__description">
          <span
            class="section-about__font section-about__font--description"
            v-html="tp(description)"
          />
        </p>
      </div>
    </div>

    <NuxtMarquee class="section-about__marquee">
      <img
        v-for="(item, index) in aboutMarqueeConfig"
        :key="index"
        :src="item.path"
        alt=""
        loading="lazy"
        width="350"
        height="228"
        class="section-about__image section-about__image--marquee"
      />
    </NuxtMarquee>

    <div class="container">
      <p class="section-about__benefits">
        <span
          class="section-about__font section-about__font--benefits"
          v-html="tp(benefits)"
        />
      </p>

      <div class="section-about__buttons">
        <UiButton
          :text="$t('buttons.touch')"
          type="button"
          theme="purple"
          class="section-about__button"
          @click="openContactModal"
        />

        <UiButton
          :text="$t('buttons.benefits')"
          type="button"
          theme="purple-outlined"
          class="section-about__button"
          @click="scrollToBenefits"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$description-font-size: 50;
$description-font-size-mobile: 20;

.section-about {
  $parent: &;

  &__inner {
    padding-block: 0;
    padding-inline: em(5);
  }

  &__description {
    text-align: center;
    margin-bottom: em(110);

    @include media-breakpoint-desktop {
      padding-block: 0;
      padding-inline: em(97);
    }

    @include media-breakpoint-mobile {
      margin-bottom: em(57);
    }
  }

  :deep(#{$parent}__icon) {
    display: inline-block;
  }

  :deep(#{$parent}__icon--monsters) {
    width: em(38, $description-font-size);
    transform: translateY(em(8, $description-font-size));

    @include media-breakpoint-mobile {
      width: em(18, $description-font-size-mobile);
      transform: translateY(em(4, $description-font-size-mobile));
    }
  }

  :deep(#{$parent}__icon--train) {
    width: em(48, $description-font-size);

    @include media-breakpoint-mobile {
      width: em(23, $description-font-size-mobile);
      transform: translateY(em(2, $description-font-size-mobile));
    }
  }

  :deep(#{$parent}__icon--asia) {
    width: em(46, $description-font-size);

    @include media-breakpoint-mobile {
      width: em(22, $description-font-size-mobile);
      transform: translateY(em(1, $description-font-size-mobile));
    }
  }

  :deep(#{$parent}__icon--middle-east) {
    transform: translateY(em(5, $description-font-size));
    width: em(50, $description-font-size);

    @include media-breakpoint-mobile {
      width: em(24, $description-font-size-mobile);
      transform: translateY(em(4, $description-font-size-mobile));
    }
  }

  :deep(#{$parent}__icon--worldwide) {
    transform: translateY(em(3, $description-font-size));
    width: em(41, $description-font-size);

    @include media-breakpoint-mobile {
      width: em(20, $description-font-size-mobile);
      transform: translateY(em(4, $description-font-size-mobile));
    }
  }

  &__marquee {
    margin-bottom: em(114);

    @include media-breakpoint-mobile {
      margin-bottom: em(58);
    }

    @include rtl {
      direction: ltr;
      transform: scaleX(-1);
    }
  }

  &__image {
    &--marquee {
      width: em(350);
      height: auto;
      margin-inline-start: em(38);
      object-fit: cover;
      border-radius: em(8);

      @include media-breakpoint-mobile {
        width: em(176);
        height: em(115);
        margin-inline-start: em(19);
        overflow: hidden;
      }
    }
  }

  &__benefits {
    margin-bottom: em(77);
    text-align: center;

    @include media-breakpoint-desktop {
      padding-block: 0;
      padding-inline: em(457);
    }

    @include media-breakpoint-mobile {
      margin-bottom: em(46);
    }
  }

  &__font {
    &--description {
      font-size: em($description-font-size);
      font-weight: $font-weight-light;

      @include media-breakpoint-mobile {
        font-size: em($description-font-size-mobile);
      }
    }

    &--benefits {
      font-weight: $font-weight-light;
      font-size: em(36);

      @include media-breakpoint-mobile {
        font-size: em(20);
      }
    }

    :deep(#{$parent}__font--accent),
    &--accent,
    :deep(#{$parent}__font--semi-bold),
    &--semi-bold {
      font-weight: $font-weight-semi-bold;
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;

    @include media-breakpoint-mobile {
      flex-direction: column;
      align-items: center;
    }
  }

  &__button {
    & + & {
      @include media-breakpoint-desktop {
        margin-inline-start: em(18);
      }

      @include media-breakpoint-mobile {
        margin-top: em(14);
      }
    }
  }
}
</style>
